import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Disclosure from '../../core/components/Disclosure.tsx';
import SwitchRow from '../../core/components/SwitchRow.tsx';

const PREFIX = 'configPage.document';

export interface ValidationCheckGroup {
    name: string;
    checks: string[];
}

interface ValidationChecksFieldProps {
    value?: string[];
    onValueChange: (value: string[]) => void;
    onBlur?: () => void;
    groups: ValidationCheckGroup[];
    renderSettings?: (check: string) => React.ReactNode;
    getCheckFieldPath?: (check: string) => string | null;
}

// experiments/validation-checks, B1: closed groups name the checks that are on; B1a6: the field a check flags
const ValidationChecksField = ({
    value = [],
    onValueChange,
    onBlur,
    groups,
    renderSettings,
    getCheckFieldPath,
}: ValidationChecksFieldProps) => {
    const { t } = useTranslation('config');
    const [openGroups, setOpenGroups] = useState<string[]>([]);
    const hasOpenedInitialGroups = useRef(false);

    // the saved checks arrive after the first render (form reset), so the groups to open are picked once they're there
    useEffect(() => {
        if (hasOpenedInitialGroups.current || !value.length) return;
        hasOpenedInitialGroups.current = true;
        setOpenGroups(
            groups.filter(({ checks }) => checks.some((check) => value.includes(check))).map(({ name }) => name)
        );
    }, [value, groups]);

    const handleGroupToggle = (name: string, isOpen: boolean) =>
        setOpenGroups((current) => {
            const others = current.filter((item) => item !== name);
            return isOpen ? [...others, name] : others;
        });

    const toggle = (check: string, isOn: boolean) => {
        onValueChange(isOn ? [...value, check] : value.filter((item) => item !== check));
        onBlur?.();
    };

    return (
        <Disclosure.Group>
            {groups.map(({ name, checks }) => {
                const onChecks = checks.filter((check) => value.includes(check));

                return (
                    <Disclosure
                        key={name}
                        open={openGroups.includes(name)}
                        onOpenChange={(isOpen) => handleGroupToggle(name, isOpen)}
                        title={t(`${PREFIX}.validationCheckGroups.${name}`)}
                        aside={t(`${PREFIX}.validationCheckGroupCount`, {
                            on: onChecks.length,
                            total: checks.length,
                        })}
                        summary={
                            onChecks.length
                                ? onChecks.map((check) => t(`${PREFIX}.validationCheckNames.${check}`)).join(' · ')
                                : t(`${PREFIX}.validationCheckGroupNoneActive`)
                        }
                    >
                        {checks.map((check) => {
                            const settings = value.includes(check) ? renderSettings?.(check) : null;
                            const fieldPath = getCheckFieldPath?.(check);

                            return (
                                <SwitchRow
                                    key={check}
                                    label={t(`${PREFIX}.validationCheckNames.${check}`)}
                                    description={t(`${PREFIX}.validationCheckDescriptions.${check}`)}
                                    aside={
                                        fieldPath && (
                                            <span className="rounded bg-surface-medium px-1.5 text-xs text-content-medium">
                                                {fieldPath}
                                            </span>
                                        )
                                    }
                                    value={value.includes(check)}
                                    onValueChange={(isOn) => toggle(check, isOn)}
                                >
                                    {settings}
                                </SwitchRow>
                            );
                        })}
                    </Disclosure>
                );
            })}
        </Disclosure.Group>
    );
};

export default ValidationChecksField;
